import React, { useState } from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import styled from "@emotion/styled";

import Typography from "../mui/Typography";
import Button from "../mui/Button";
import Stack from "../mui/Stack";
import Box from "../mui/Box";

// common components
import MagicLoadingBox from "../common/MagicLoadingBox";

// Define the type for each series item
interface SeriesData {
  name: string;
  data: number[];
  color: string;
  type?: string;
}

// Define the props for the component
interface QuarterWiseMedalsChartProps {
  seriesData: SeriesData[] | unknown;
  isLoading: boolean;
}

const QuarterWiseMedalsGraph: React.FC<QuarterWiseMedalsChartProps> = ({
  seriesData,
  isLoading,
}) => {
  const [chartType, setChartType] = useState<string>("line"); // track the selected graph type

  const graphTypes = [
    { type: "line", label: "Line graph" },
    { type: "column", label: "Bar graph" },
  ];

  const legendBtns = [
    { color: "#8047E7", label: "Platinum", id: 1 },
    { color: "#E7B147", label: "Gold", id: 2 },
    { color: "#BEBEBE", label: "Silver", id: 3 },
    { color: "#E79447", label: "Bronze", id: 4 },
  ];

  // handle Loading state of api data
  if (isLoading) return <MagicLoadingBox height={500} />;

  // Function to update the chart type based on Button click
  const handleGraphTypeChange = (type: string) => {
    setChartType(type);
  };

  // @ts-ignore
  const updatedSeriesData: SeriesData[] = (seriesData ?? []).map(
    (series: any) => ({
      ...series,
      type: chartType,
      data: series.data.map((entry: { count: number; percentage: number }) => ({
        y: entry.count, // Use `y` for count
        percentage: entry.percentage, // Store percentage for tooltip
      })),
    })
  );

  const options: Highcharts.Options = {
    chart: {
      type: "column", // Replace 'chartType' with the actual chart type if necessary
      backgroundColor: "white",
      height: 300,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: ["Quarter 1", "Quarter 2", "Quarter 3", "Quarter 4"],
    },
    yAxis: {
      title: {
        text: "",
      },
      min: 0,
      max: 100,
      tickInterval: 20,
      minPadding: 0.1,
      maxPadding: 0.1,
    },
    tooltip: {
      enabled: true,
      useHTML: true, // Enables custom HTML for the tooltip
      style: {
        fontSize: "12px",
        color: "gray",
        fontWeight: "500",
        textAlign: "center",
      },
      formatter: function () {
        const percentage = this?.point?.percentage ?? 0;
        const count = this?.point?.y ?? 0;
        const category = this?.series?.name ?? "";
        const color = this?.series?.color ?? "#000"; // Default to black if color is not defined
        return `
          <div style="
            display: inline-block;
            position: relative;
            color: #344054;
            border-radius: 8px;
            font-weight: 500;
            padding-bottom:10px;
          ">
           <span style="
          display: inline-block;
          width: 8px;
          height: 8px;
          background-color: ${color};
          border-radius: 50%;
          margin-right: 8px;
        "></span>
            <strong>${category}</strong> <br/>
            Count: ${count} <br/>
            Percentage: ${percentage.toFixed(0)}%<br/>
          </div>`;
      },
      positioner: function (labelWidth, labelHeight, point) {
        let tooltipX = point.plotX + this.chart.plotLeft - labelWidth / 2;
        let tooltipY = point.plotY + this.chart.plotTop - labelHeight - 30; // Add 20px margin from the bottom
        return { x: tooltipX, y: tooltipY };
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false, // Hides the Highcharts.com logo
    },
    exporting: {
      enabled: false,
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          useHTML: true,
          style: {
            fontSize: "12px",
            color: "#344054",
            fontWeight: "500",
            textAlign: "center",
          },
          formatter: function () {
            const y = this?.y ?? 0;
            const percentage = this?.percentage ?? 0;
            return `
              <div style="
                display: inline-block;
                position: relative;
                background-color: #ffffff;
                color: #344054;
                padding: 5px;
                border-radius: 8px;
                font-weight: 500;
              ">
                ${y} <br/>
                (${percentage.toFixed(0)}%) <br/>
                <div style="
                  content: '';
                  position: absolute;
                  bottom: -8px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 0;
                  height: 0;
                  border-left: 8px solid transparent;
                  border-right: 8px solid transparent;
                  border-top: 8px solid #ffffff;
                "></div>
              </div>`;
          },
        },
      },
    },

    series: updatedSeriesData
      ? JSON.parse(JSON.stringify(updatedSeriesData))
      : [],
  };

  return (
    <StyledStack>
      {/* Render title and buttons */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack mb="24px">
          <StyledGraphHeading>Quarter wise medals </StyledGraphHeading>

          <StyledLegendContainer>
            {legendBtns?.map((item, index) => (
              <React.Fragment key={index}>
                <StyledLegendBox sx={{ backgroundColor: item.color }} />
                <StyledLegendText>{item.label}</StyledLegendText>
              </React.Fragment>
            ))}
          </StyledLegendContainer>
        </Stack>

        <Stack direction="row">
          {graphTypes.map(
            (graph: { type: string; label: string }, index: number) => (
              <StyledBtn
                key={index}
                onClick={() => handleGraphTypeChange(graph?.type)}
                sx={{
                  borderBottom:
                    chartType === graph.type
                      ? "2px solid #333"
                      : "2px solid #D0D5DD",
                  backgroundColor:
                    chartType === graph.type ? "#FFF1B7" : "#FFF",
                  color: chartType === graph.type ? "#333" : "#6D6D6D",

                  "&:hover": {
                    borderBottom:
                      chartType === graph.type
                        ? "2px solid #333"
                        : "2px solid #D0D5DD",
                    backgroundColor:
                      chartType === graph.type ? "#FFF1B7" : "#FFF",
                    color: chartType === graph.type ? "#333" : "#6D6D6D",
                    boxShadow: "none",
                  },
                }}
              >
                {graph.label}
              </StyledBtn>
            )
          )}
        </Stack>
      </Stack>

      <Box sx={{ height: "2px", backgroundColor: "#D7D7D7", mb: "20px" }} />

      {updatedSeriesData?.[0]?.data.length !== 0 &&
      updatedSeriesData?.[1]?.data.length !== 0 ? (
        <Box>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <Typography>No data found</Typography>
        </Box>
      )}
    </StyledStack>
  );
};

const StyledStack = styled(Stack)({
  width: "100%",
  margin: "0 auto",
  border: "1px solid #D7D7D7",
  borderRadius: "16px",
  padding: "20px",
  boxSizing: "border-box",
  overflow: "hidden",
  backgroundColor: "white",
  position: "relative",
  marginBottom: "24px",
});

const StyledGraphHeading = styled(Typography)({
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "16.94px",
  marginBottom: "13px",
});

const StyledLegendContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  gap: "8px",
  alignItems: "center",
});

const StyledLegendBox = styled(Box)({
  borderRadius: "16px",
  width: "20px",
  height: "20px",
  padding: "4px 16px",
});

const StyledLegendText = styled(Typography)({
  margin: 0,
  fontSize: "14px",
  color: "#4E4E4E",
  fontWeight: 500,
  lineHeight: "18px",
});

const StyledBtn = styled(Button)({
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "none",
  fontSize: "14px",
  width: "117px",
  height: "40px",
  borderRadius: "0px",
  textTransform: "inherit",
});

export default QuarterWiseMedalsGraph;
